import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  type OnInit,
} from '@angular/core';
import {
  Questionnaire,
  QuestionInPage,
  QuestionnaireAnswer,
  QuestionnairePage,
} from 'src/app/core/models/questionnaire.model';
import {
  isEffectivelyElective,
  isQuestionSkippableInSimpleQuestionnaire,
} from 'src/app/shared/utils/elective';
import { PageErrors } from '../questionnaire-answer.component';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  map,
  Observable,
  Subject,
  Subscription,
  tap,
} from 'rxjs';
import { runConditions } from 'src/app/shared/utils/hideif';
import { QuestionAnswer } from '../../../simple/question/question.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { EligibilityInfo } from '../core/eligibility.service';

@Component({
  selector: 'questionnaire-answer-page',
  templateUrl: './questionnaire-answer-page.component.html',
  styleUrls: ['./questionnaire-answer-page.component.scss'],
})
export class QuestionnaireAnswerPageComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() page!: QuestionnairePage;
  @Input() questionnaire: Questionnaire | undefined;
  /** CPK-4846: resolved by the parent from cp_case.configuration.simple_questionnaire. */
  @Input() caseAllowsSimpleQuestionnaire = false;
  @Input() questionnaireAnswer$!: Observable<QuestionnaireAnswer | null>;
  @Input() ineligibilityInfo!: EligibilityInfo;
  @Input() user: any;

  questionnaireAnswer!: QuestionnaireAnswer | null;

  changeAnswer$ = new Subject<void>();

  @Output() onChangeQuestionnaireAnswer =
    new EventEmitter<QuestionnaireAnswer>();
  @Output() onChangePageValid = new EventEmitter<PageErrors>();
  @Output() onLoadingPage = new EventEmitter<boolean>(false);

  questionsMap: Record<string, QuestionInPage> = {};
  hideMap: Record<string, boolean> = {};
  blockedByAnswerBehaviorSlug: string | null = null;

  pageLoading$ = new BehaviorSubject<boolean>(true);
  loading = true;
  subscriptions: Subscription[] = [];

  qa$!: Observable<QuestionnaireAnswer>;

  supressNextChange = false;
  lastQuestions: Record<string, QuestionInPage> = {};
  hasScrolledToTop = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit() {
    this._initObservables();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['page']) {
      if (this.page) {
        this.setAnswersOnQuestionsMap();
        this.page?.cp_question_in_page.sort((question1, question2) => {
          const priority1 = question1.priority || 0;
          const priority2 = question2.priority || 0;
          return priority2 - priority1;
        });
      }
    }
  }
  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private _initObservables() {
    const answers$ = this.questionnaireAnswer$
      .pipe(
        filter((e) => !!e),
        debounceTime(100)
      )
      .subscribe({
        next: (questionnaireAnswer) => {
          if (this.supressNextChange) {
            this.supressNextChange = false;
            return;
          }

          this.questionnaireAnswer = questionnaireAnswer;
          if (Object.keys(this.questionsMap).length === 0)
            this.setQuestionMap();
          this.setAnswersOnQuestionsMap();
          this.updateQuestionHideMap();
          this.checkValidatePage();

          this.page?.cp_question_in_page.sort((question1, question2) => {
            const priority1 = question1.priority || 0;
            const priority2 = question2.priority || 0;
            return priority2 - priority1;
          });

          this.cdr.markForCheck();
          this.cdr.detectChanges();

          this.loading = false;
          this.pageLoading$.next(false);

          this.scrollToTopOnFirstRenderedContent();
        },
      });

    const changeAnswerSub$ = this.changeAnswer$
      .pipe(
        debounceTime(150),
        tap(() => {
          if (!this.questionnaireAnswer) this.pageLoading$.next(false);
        })
      )
      .subscribe({
        next: () => {
          this.questionnaireAnswer &&
            this.onChangeQuestionnaireAnswer.emit(this.questionnaireAnswer);
        },
      });

    const pageLoadingSub$ = this.pageLoading$
      .pipe(debounceTime(150))
      .subscribe({
        next: (loading) => {
          // console.log('Page Loading => ', loading);
          this.onLoadingPage.emit(loading);
          this.cdr.markForCheck();
          this.cdr.detectChanges();
        },
      });

    this.qa$ = this.questionnaireAnswer$.pipe(
      filter((e) => !!e),
      map((e) => e as QuestionnaireAnswer)
    );

    this.subscriptions.push(answers$, changeAnswerSub$, pageLoadingSub$);
  }
  private scrollToTopOnFirstRenderedContent() {
    if (this.hasScrolledToTop) {
      return;
    }
    this.hasScrolledToTop = true;
    requestAnimationFrame(() => {
      this.elementRef.nativeElement.scrollIntoView({
        block: 'start',
        behavior: 'auto',
      });
    });
  }
  private setQuestionMap() {
    if (this.page.cp_question_in_page) {
      this.page.cp_question_in_page.forEach((q) => {
        // Only ever coerces elective to `true` when the Simple Questionnaire session
        // makes it skippable — never touches the persisted value otherwise, so a
        // question with `elective: undefined` still renders exactly as before.
        const elective = isQuestionSkippableInSimpleQuestionnaire(
          q.cp_question,
          this.caseAllowsSimpleQuestionnaire
        )
          ? true
          : q.cp_question.elective;

        this.questionsMap[q.cp_question.slug] = {
          ...q,
          cp_question: {
            ...q.cp_question,
            answer: { value: null },
            elective,
          },
        };
      });
    } else {
      this.questionsMap = {};
    }
    this.hideMap = {};
    Object.keys(this.questionsMap).forEach((key) => {
      this.hideMap[key] = false;
    });
  }
  private setAnswersOnQuestionsMap() {
    if (this.questionnaireAnswer) {
      const answers = this.questionnaireAnswer.answers;

      Object.entries(this.questionsMap).forEach(([key, value]) => {
        const questionSlug = value.cp_question.slug;
        const savedAnswer = answers[questionSlug];
        const prefillAnswer = this.buildProfilePrefillAnswer(value.cp_question);
        const answer = savedAnswer || prefillAnswer || { value: null };

        if (!savedAnswer && prefillAnswer) {
          this.questionnaireAnswer!.answers[questionSlug] = prefillAnswer;
        }

        this.questionsMap[key] = {
          ...this.questionsMap[key],
          cp_question: {
            ...this.questionsMap[key].cp_question,
            answer: answer,
          },
        };
      });
    }
  }

  /**
   * Pre-fills a never-answered question from the client's PII when the
   * question's fillInfo targets the same profile field — e.g. an address
   * answered on one claim shows up already filled on another claim's
   * questionnaire, instead of only writing PII on answer (fillInfo was
   * write-only until now).
   */
  private buildProfilePrefillAnswer(
    question: any
  ): { value: any; addtional_information?: any } | null {
    const fillInfo = question?.configuration?.fillInfo;
    if (!fillInfo || fillInfo.target !== 'profile' || !fillInfo.field) {
      return null;
    }

    const profileValue = this.resolveProfileFieldValue(fillInfo.field);
    if (profileValue === undefined || profileValue === null || profileValue === '') {
      return null;
    }

    if (
      question.response_type === 'WORLD_ADDRESS' ||
      question.response_type === 'UK_ADDRESS'
    ) {
      if (typeof profileValue !== 'object') return null;
      const { streetNumber, streetName, city, state, postalCode, country } =
        profileValue;
      const composed = [streetNumber, streetName, city, state, postalCode, country]
        .filter(Boolean)
        .join(', ');
      return { value: composed || null, addtional_information: profileValue };
    }

    if (typeof profileValue === 'object') return null;
    return { value: profileValue };
  }

  private resolveProfileFieldValue(field: string): any {
    const pii = this.user?.pii;
    if (!pii) return undefined;

    const [base, nested] = field.split('.');
    let baseValue: any;
    switch (base) {
      case 'first_name':
        baseValue = pii.attributes?.first_name;
        break;
      case 'surname':
        baseValue = pii.attributes?.surname;
        break;
      case 'date_of_birth':
        baseValue = pii.date_of_birth;
        break;
      case 'legal_name':
        baseValue = pii.legal_name;
        break;
      case 'id_document_type':
        baseValue = pii.id_document_type;
        break;
      case 'id_document_value':
        baseValue = pii.id_document_value;
        break;
      case 'contacts':
        baseValue = pii.contacts;
        break;
      case 'primary_address':
        baseValue = pii.primary_address;
        break;
      default:
        baseValue = undefined;
    }

    return nested ? baseValue?.[nested] : baseValue;
  }
  private updateQuestionHideMap() {
    let didClearAny = false;

    this.page.cp_question_in_page.forEach((qs) => {
      const questionLogic = qs.question_logic;

      if (!questionLogic || !questionLogic['hide_if']) {
        return;
      }

      const hideIf = questionLogic['hide_if'];
      const slug = qs.cp_question.slug;
      const wasHidden = this.hideMap[slug];

      const shouldHide = runConditions(hideIf, this.questionnaireAnswer);

      if (shouldHide) {
        this.hideMap[slug] = true;

        // Only clear the answer on transition visible → hidden
        // Avoids re-emitting { value: null } every cycle for questions that
        // are already hidden, which caused desync on DATE / SIGNATURE types
        if (!wasHidden) {
          this.questionnaireAnswer!.answers[slug] = { value: null };
          didClearAny = true;
        }
      } else {
        this.hideMap[slug] = false;
      }
    });

    if (didClearAny) {
      this.supressNextChange = true;
      this.changeAnswer$.next();
    }
  }
  private checkValidatePage() {
    const errors: PageErrors = {
      page: this.page.id,
      errors: {},
      isValid: true,
    };

    // Detect if any question is blocked by answer_behavior
    this.blockedByAnswerBehaviorSlug = null;
    for (const q of this.page.cp_question_in_page) {
      if (q.cp_question.isBlockedByAnswerBehavior) {
        this.blockedByAnswerBehaviorSlug = q.cp_question.slug;
        break;
      }
    }

    this.page.cp_question_in_page.forEach((q) => {
      this._checkQuestionValidity(q, errors);
    });

    this.onChangePageValid.emit(errors);
  }

  private _checkQuestionValidity(q: QuestionInPage, errors: PageErrors) {
    // Primeiro verificamos se a questão deve ser validada
    // Questões ocultas ou de tipo explicação não precisam de validação
    if (
      this.hideMap[q.cp_question.slug] ||
      q.cp_question.response_type == 'EXPLANATION'
    ) {
      return;
    }

    const questionSlug = q.cp_question.slug;
    const answer = this.questionnaireAnswer?.answers[questionSlug];
    const isRequired = !isEffectivelyElective(q.cp_question, this.caseAllowsSimpleQuestionnaire);
    const wasModified = this.lastQuestions?.[questionSlug];

    // Verificação de campo obrigatório sem resposta (null, undefined ou string vazia)
    const isEmpty = answer?.value === null || answer?.value === undefined ||
      (typeof answer?.value === 'string' && answer.value.trim() === '');
    if (isRequired && isEmpty) {
      errors.isValid = false;
      if (wasModified) {
        errors.errors[questionSlug] = `${this.translate.instant(
          'CheckTheField'
        )} "${q.cp_question.name}"`;
      }
      return;
    }

    // Verificação da validade da resposta (se tiver resposta, deve ser válida)
    if (q.cp_question.isValid === false) {

      errors.isValid = false;
      // answer_behavior blocks only show the inline banner — skip the red error list
      if (wasModified && !q.cp_question.isBlockedByAnswerBehavior) {
        if (q.cp_question.messageError === 'CPFLeakCheckMustBeChecked') {
          errors.errors[questionSlug] = `${this.translate.instant(
            'CheckTheField'
          )} "${q.cp_question.name}"`;
        } else {
          errors.errors[questionSlug] = q.cp_question.messageError
            ? `${this.translate.instant('Question')} "${
                q.cp_question.name
              }" ${this.translate.instant(q.cp_question.messageError)}`
            : `${this.translate.instant('Question')} "${
                q.cp_question.name
              }" ${this.translate.instant('is invalid')}`;
        }
      }
    }

    // Se não é obrigatório e não tem resposta, não precisa validar
    if (!isRequired && !answer) {
      return;
    }
  }

  public onChangeAnswer(answer: QuestionAnswer) {
    this.pageLoading$.next(true);
    if (!this.questionnaireAnswer) {
      this.pageLoading$.next(false);
      return;
    }

    const index = this.page.cp_question_in_page.findIndex(
      (q) => q.cp_question.slug === answer.question.slug
    );
    if (index === -1) {
      this.pageLoading$.next(false);
      return;
    }
    // CPK-4894: this.page is the parent's own page object, and answer.question is the
    // rendering copy from setQuestionMap(), whose elective is the effective value. Keep
    // the persisted elective here, or a touched can_skip_question question reads as
    // elective in the parent and hasSkippedRequiredPending() submits it SUBMITTED.
    const persistedElective = this.page.cp_question_in_page[index].cp_question.elective;
    this.page.cp_question_in_page[index].cp_question = {
      ...answer.question,
      elective: persistedElective,
    };

    this.questionnaireAnswer.answers[answer.question.slug] = answer.answer;

    this.lastQuestions[answer.question.slug] =
      this.page.cp_question_in_page[index];

    this.checkValidatePage();
    this.changeAnswer$.next();
    if (answer.question.isValid) {
      this.pageLoading$.next(false);
    }
  }
}
